/**
 * @fileoverview Compose the explorer's selected-document summary, preview, and action signifiers. `DocumentMedia.js` owns media choice and `DocumentFileTypeIcon.js` owns file signifiers. The parent supplies selection/capabilities and owns mutations, keeping preview interaction separate from canonical document state.
 */

import React from 'react';
import AppIcon from '../shared/AppIcon';
import DocumentFileTypeIcon, {
  documentFileType,
  documentTypeLabel,
} from './DocumentFileTypeIcon';
import { formatCreated, formatSize } from './archiveReviewUtils';
import DocumentMedia from './DocumentMedia';
import { documentFirstPagePreviewSource } from './documentSources';
import { MillerColumn, MillerColumnHeader } from './MillerColumnExplorer';
import './DocumentExplorerPreviewColumn.css';

export { documentRevisionId, documentDownloadSource, documentPreviewSource, documentFirstPagePreviewSource } from './documentSources';



function DocumentExplorerPreviewColumn({ document, className = '', onOpen }) {
  if (!document) return null;
  const filename = document.filename || document.name || 'Untitled document';
  const type = documentFileType(document);
  const source = documentFirstPagePreviewSource(document, type);
  const modified = formatCreated(document.modified_at || document.updated_at || document.first_seen_at);
  const size = formatSize(document.size_bytes ?? document.size);
  const detailParts = [documentTypeLabel(document)];
  if (size !== '-') detailParts.push(size);
  if (modified.date && modified.date !== '-') detailParts.push(modified.date);

  return (
    <MillerColumn
      className={`document-explorer-preview ${className}`.trim()}
      aria-label={`Preview of ${filename}`}>
      <MillerColumnHeader>
        <AppIcon icon="pi pi-eye" aria-hidden="true" />
        <strong>Preview</strong>
      </MillerColumnHeader>
      <div className="document-explorer-preview__body">
        <div
          className={`document-explorer-preview__media is-${type}${onOpen ? ' is-openable' : ''}`}
          role={onOpen ? 'button' : undefined}
          tabIndex={onOpen ? 0 : undefined}
          aria-label={onOpen ? `Open ${filename}` : undefined}
          title={onOpen ? `Double-click to open ${filename}` : undefined}
          onDoubleClick={onOpen ? () => onOpen(document) : undefined}
          onKeyDown={onOpen ? (event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            onOpen(document);
          } : undefined}>
          <DocumentMedia document={document} type={type} source={source} thumbnail />
        </div>
        <div className="document-explorer-preview__details">
          <strong title={filename}>{filename}</strong>
          <small>{detailParts.join(' · ')}</small>
        </div>
      </div>
    </MillerColumn>
  );
}

export default DocumentExplorerPreviewColumn;
